/**
 * Route -> human-readable page name lookup, used by the Report Issue feature
 * to label which page a complaint was raised from. Mirrors the routes
 * registered in src/App.js.
 */
const ROUTE_TITLES = [
    { pattern: /^\/institutionsales$/, title: 'Institution Sales' },
    { pattern: /^\/orderlist$/, title: 'Order List' },
    { pattern: /^\/orderview\/[^/]+$/, title: 'Order View' },
    { pattern: /^\/orderedit\/[^/]+$/, title: 'Order Edit' },
    { pattern: /^\/assignproduct$/, title: 'Assign Product' },
    { pattern: /^\/viewassignedproduct$/, title: 'View Assigned Product' },
    { pattern: /^\/feedback$/, title: 'Feedback' },
    { pattern: /^\/itemreport$/, title: 'Item Report' },
    { pattern: /^\/itemonlinereport$/, title: 'Item Online Report' },
    { pattern: /^\/salesreport$/, title: 'Sales Report' },
    { pattern: /^\/billreport$/, title: 'Bill Report' },
    { pattern: /^\/billview\/[^/]+$/, title: 'Bill View' },
    { pattern: /^\/location$/, title: 'Location' },
    { pattern: /^\/categorymaster$/, title: 'Category Master' },
    { pattern: /^\/productcategory$/, title: 'Product Category' },
    { pattern: /^\/productmaster$/, title: 'Product Master' },
    { pattern: /^\/productlist$/, title: 'Product List' },
    { pattern: /^\/productedit\/[^/]+$/, title: 'Product Edit' },
    { pattern: /^\/productmultibranch$/, title: 'Product Multi Branch' },
    { pattern: /^\/productmultibranchlist$/, title: 'Product Multi Branch List' },
    { pattern: /^\/settings$/, title: 'Settings' },
    { pattern: /^\/applicationsetting$/, title: 'Application Setting' },
    { pattern: /^\/clients$/, title: 'Clients' },
    { pattern: /^\/branch$/, title: 'Branch' },
    { pattern: /^\/branchlist$/, title: 'Branch List' },
    { pattern: /^\/branchedit\/[^/]+$/, title: 'Branch Edit' },
    { pattern: /^\/users$/, title: 'Users' },
    { pattern: /^\/masters$/, title: 'Masters' },
    { pattern: /^\/masterlist$/, title: 'Master List' },
    { pattern: /^\/smsmanage$/, title: 'SMS Manage' },
    { pattern: /^\/smsdetaillist$/, title: 'SMS Detail List' },
    { pattern: /^\/uom$/, title: 'UOM' },
    { pattern: /^\/taxpercent$/, title: 'Tax Percent' },
    { pattern: /^\/platform$/, title: 'Platform' },
    { pattern: /^\/company$/, title: 'Company' },
    { pattern: /^\/modulegroup$/, title: 'Module Group' },
    { pattern: /^\/modulemenu$/, title: 'Module Menu' },
    { pattern: /^\/usermodules$/, title: 'User Modules' },
    { pattern: /^\/roles$/, title: 'Roles' },
    { pattern: /^\/sections$/, title: 'Sections' },
    { pattern: /^\/notifications$/, title: 'Notifications' },
    { pattern: /^\/taxinvoice$/, title: 'Tax Invoices' },
    { pattern: /^\/buyermaster$/, title: 'Buyer Master' },
    { pattern: /^\/sellermaster$/, title: 'Seller Master' },
    { pattern: /^\/mobilebillingapp$/i, title: 'Mobile Billing App' },
    { pattern: /^\/billreports$/, title: 'Bill Reports' },
    { pattern: /^\/creditreport$/, title: 'Credit Report' },
    { pattern: /^\/creditsettelementreport$/, title: 'Credit Settlement Report' },
    { pattern: /^\/excelexport$/, title: 'Excel Export' },
    { pattern: /^\/advancereport$/, title: 'Advance Report' },
    { pattern: /^\/billreceipt\/[^/]+\/[^/]+$/, title: 'Bill Receipt' },
    { pattern: /^\/salesreports$/, title: 'Sales Reports' },
    { pattern: /^\/salesreporta$/, title: 'Sales Reporta' },
    { pattern: /^\/categorywisereports$/, title: 'Category Wise Reports' },
    { pattern: /^\/categoryonlinereport$/, title: 'Category Online Report' },
    { pattern: /^\/waiterreport$/, title: 'Waiter Report' },
    { pattern: /^\/dashboardTest$/i, title: 'Dashboard' },
    { pattern: /^\/cleardata$/, title: 'Clear Data' },
    { pattern: /^\/voucheradd$/, title: 'Add Voucher' },
    { pattern: /^\/voucherlist$/, title: 'Voucher List' },
    { pattern: /^\/expenseadd$/, title: 'Add Expense' },
    { pattern: /^\/expenseslist$/, title: 'Expenses List' },
    { pattern: /^\/categorydialog$/, title: 'Category Dialog' },
    { pattern: /^\/categoryproductdialog$/, title: 'Category Product Dialog' },
    { pattern: /^\/odcedit(\/[^/]+)?$/, title: 'ODC Edit' },
    { pattern: /^\/odclist$/, title: 'ODC List' },
    { pattern: /^\/odcorderlist$/, title: 'ODC Order List' },
    { pattern: /^\/odcview$/, title: 'ODC View' },
    { pattern: /^\/odcsales$/, title: 'ODC Sales' },
    { pattern: /^\/ordcsales$/, title: 'ODC Sales' },
    { pattern: /^\/parentmasters$/, title: 'Parent Masters' },
    { pattern: /^\/submasters$/, title: 'Sub Masters' },
    { pattern: /^\/issue-reports$/, title: 'Issues Raised' },
    { pattern: /^\/livedata$/, title: 'Live Data' },
    { pattern: /^\/gsbillingendpoints$/, title: 'GS Billing Endpoints' },
    { pattern: /^\/vikrayaendpoints$/, title: 'Vikraya Endpoints' },
    { pattern: /^\/rootsalesendpoints$/, title: 'Rootsales Endpoints' },
    { pattern: /^\/santheendpoints$/, title: 'Santhe Endpoints' },
    { pattern: /^\/apiendpoints$/, title: 'API Endpoints' },
    { pattern: /^\/clientreportchecking$/i, title: 'Client Reports' },
    { pattern: /^\/POSDashboard$/i, title: 'POS Dashboard' }
];

const prettifyPath = (pathname) => {
    const segment = String(pathname || '')
        .split('/')
        .filter(Boolean)[0];
    if (!segment) return 'Home';
    return segment
        .replace(/[-_]/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Returns a human-readable page name for a given pathname (e.g. from useLocation().pathname). */
export const getPageName = (pathname) => {
    const path = String(pathname || '/');
    const match = ROUTE_TITLES.find((r) => r.pattern.test(path));
    return match ? match.title : prettifyPath(path);
};
